import type { KeyboardEvent } from 'react';
import { useState } from 'react';

import { cn } from '@/utils/classname';

// Shake animation for duplicate detection
const shakeKeyframes = `
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

.animate-shake {
  animation: shake 300ms ease-in-out;
}
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleId = 'multi-value-input-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = shakeKeyframes;
    document.head.appendChild(style);
  }
}

export interface MultiValueInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  editable: boolean;
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
}

export const MultiValueInput = ({
  values,
  onChange,
  editable,
  placeholder = 'Type and press Enter...',
  className,
  'aria-label': ariaLabel,
}: MultiValueInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [shakingIndex, setShakingIndex] = useState<number | null>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedValue = inputValue.trim();

      // Ignore empty input
      if (!trimmedValue) return;

      // Check for duplicate
      const duplicateIndex = values.findIndex(v => v === trimmedValue);
      if (duplicateIndex !== -1) {
        // Trigger shake animation
        setShakingIndex(duplicateIndex);
        setTimeout(() => setShakingIndex(null), 300);
        return;
      }

      // Add new value
      onChange([...values, trimmedValue]);
      setInputValue('');
    }
  };

  const handleRemove = (indexToRemove: number) => {
    onChange(values.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="space-y-2">
      {editable && (
        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            'border-chariot-stroke w-full rounded border px-1 py-0.5 font-mono text-[9px]',
            className
          )}
          aria-label={ariaLabel}
        />
      )}

      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((value, index) => (
            <button
              key={index}
              type="button"
              onClick={() => editable && handleRemove(index)}
              disabled={!editable}
              className={cn(
                'rounded px-2 py-0.5 font-mono text-[9px]',
                'bg-blue-500/10 text-blue-600',
                editable &&
                  'cursor-pointer transition-colors hover:bg-blue-500/20',
                !editable && 'cursor-default',
                shakingIndex === index && 'animate-shake'
              )}
              aria-label={editable ? `Remove ${value}` : undefined}
            >
              {value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
